import React, { useCallback, useEffect, useState } from 'react';
import {
    Button, Card, Collapse, DatePicker, Descriptions, Drawer, Input, InputNumber, Select, Space, Spin, Table, Tag, Timeline, Tooltip, Typography, message,
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import tmAdminExtService from '../../services/tmAdminExtService';
import { fmtTime, toastErr } from './common';
import { StreamRef } from './opsCommon';
import StreamOpsDrawer from './StreamOpsDrawer';

const { Text } = Typography;

const STATUS = {
    queued: ['blue', '排队'], delivered: ['green', '已送达'], deferred: ['gold', '延迟'], bounced: ['red', '退信'],
    complained: ['magenta', '投诉'], rejected: ['volcano', '拒收'], failed: ['red', '失败'],
};
const EVENT_COLOR = { accepted: 'blue', delivered: 'green', deferred: 'gray', bounced: 'red', complained: 'red', failed: 'red', rejected: 'red', virus: 'red', spam_rejected: 'red' };
const HOOK_COLOR = { success: 'green', pending: 'blue', failed: 'orange', gave_up: 'red' };
const MAX_DAYS = 31;

const MessageDrawer = ({ id, onClose, onOpenStream }) => {
    const [d, setD] = useState(null);
    useEffect(() => {
        setD(null);
        if (!id) return;
        tmAdminExtService.getMessage(id).then((r) => setD(r?.data || null)).catch((e) => toastErr(e, '加载失败'));
    }, [id]);
    const m = d?.message;
    return (
        <Drawer open={!!id} onClose={onClose} width={760} title={id} destroyOnClose>
            {!d ? <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div> : (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Descriptions size="small" column={2} bordered items={[
                        { key: 'st', label: '状态', children: <Tag color={STATUS[m.status]?.[0]}>{STATUS[m.status]?.[1] || m.status}</Tag> },
                        { key: 'src', label: '来源', children: `${m.source}${d.api_key ? ` · ${d.api_key.name} (${d.api_key.prefix}…)` : ''}` },
                        { key: 'rc', label: '收件人', children: m.rcpt },
                        { key: 'mf', label: '退信地址', children: m.mail_from || '-' },
                        { key: 'fr', label: 'From', span: 2, children: m.from_header || '-' },
                        { key: 'sj', label: '主题', span: 2, children: m.subject || '-' },
                        { key: 'cat', label: '分类', children: m.category || '-' },
                        { key: 'sz', label: '大小', children: m.size ? `${m.size} B` : '-' },
                        { key: 'str', label: '发信流', children: d.stream ? <StreamRef stream={d.stream} onOpen={onOpenStream} /> : `#${m.stream_id}` },
                        { key: 'u', label: '用户', children: d.stream ? `#${d.stream.user_id} ${d.stream.username || ''} ${d.stream.email || ''}` : `#${m.user_id}` },
                        { key: 'sc', label: 'SMTP', span: 2, children: m.smtp_code ? <Text code style={{ whiteSpace: 'pre-wrap' }}>{m.smtp_code} {m.smtp_response}</Text> : '-' },
                        { key: 'tr', label: '打开/点击', children: `${m.open_count || 0} / ${m.click_count || 0}` },
                        { key: 'un', label: '退订', children: fmtTime(m.unsubscribed_at) },
                        { key: 'ct', label: '创建', children: fmtTime(m.created_at) },
                        { key: 'ut', label: '更新', children: fmtTime(m.updated_at) },
                    ]} />
                    <Card size="small" title="事件">
                        <Timeline items={(d.events || []).map((e) => ({
                            color: EVENT_COLOR[e.type] || 'gray',
                            children: (
                                <div>
                                    <Space size={6} wrap>
                                        <Text strong>{e.type}</Text>
                                        {e.data?.bounce_type && <Tag>{e.data.bounce_type}</Tag>}
                                        <Text type="secondary" style={{ fontSize: 12 }}>{fmtTime(e.at)}{e.node ? ` · ${e.node.name}` : ''}</Text>
                                    </Space>
                                    {(e.data?.smtp_code || e.data?.smtp_response) && (
                                        <div><Text code style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{e.data.smtp_code} {e.data.smtp_response}</Text></div>
                                    )}
                                    {(e.data?.mx || e.data?.ip) && <Text type="secondary" style={{ fontSize: 12 }}>MX {e.data.mx || '-'} · IP {e.data.ip || '-'}</Text>}
                                    {e.data?.virus_name && <div><Text type="danger">{e.data.virus_name}</Text></div>}
                                </div>
                            ),
                        }))} />
                        <Collapse ghost size="small" items={[{
                            key: 'raw', label: '原始事件',
                            children: <pre style={{ fontSize: 12, maxHeight: 300, overflow: 'auto', margin: 0 }}>{JSON.stringify(d.events, null, 2)}</pre>,
                        }]} />
                    </Card>
                    <Card size="small" title="Webhook 投递">
                        <Table size="small" rowKey="id" pagination={false} dataSource={d.webhook_deliveries || []} locale={{ emptyText: '无' }}
                            expandable={{ expandedRowRender: (w) => <pre style={{ fontSize: 12, margin: 0 }}>{JSON.stringify(w.payload, null, 2)}</pre> }}
                            columns={[
                                { title: '事件', dataIndex: 'event_type', width: 100 },
                                { title: '状态', dataIndex: 'status', width: 90, render: (v) => <Tag color={HOOK_COLOR[v]}>{v}</Tag> },
                                { title: '次数', dataIndex: 'attempts', width: 60 },
                                { title: '结果', key: 'r', render: (_, w) => <Tooltip title={w.url}><Text type="secondary" style={{ fontSize: 12 }}>{w.last_status_code || ''} {w.last_error || ''}</Text></Tooltip> },
                                { title: '时间', dataIndex: 'created_at', width: 150, render: fmtTime },
                            ]} />
                    </Card>
                </Space>
            )}
        </Drawer>
    );
};

const Messages = () => {
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({ total: 0, capped: false });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState({ current: 1, pageSize: 50 });
    const [draft, setDraft] = useState({});
    const [f, setF] = useState({});
    const [range, setRange] = useState([dayjs().subtract(1, 'day'), dayjs()]);
    const [openId, setOpenId] = useState(null);
    const [streamId, setStreamId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await tmAdminExtService.searchMessages({
                ...f, status: f.status?.length ? f.status.join(',') : undefined,
                since: range?.[0]?.format('YYYY-MM-DD HH:mm:ss'), until: range?.[1]?.format('YYYY-MM-DD HH:mm:ss'),
                page: page.current, per_page: page.pageSize,
            });
            setRows(r?.data?.items || []);
            setMeta({ total: r?.data?.total || 0, capped: !!r?.data?.total_capped });
            if (r?.data?.note) message.info(r.data.note);
        } catch (e) {
            toastErr(e, '查询失败');
        } finally {
            setLoading(false);
        }
    }, [f, range, page]);

    useEffect(() => { load(); }, [load]);

    const search = () => {
        const clean = Object.fromEntries(Object.entries(draft).filter(([, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && !v.length)));
        setF(clean);
        setPage((p) => ({ ...p, current: 1 }));
    };
    const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v?.target ? v.target.value.trim() : v }));

    const columns = [
        { title: '时间', dataIndex: 'created_at', width: 155, render: fmtTime },
        { title: '收件人', dataIndex: 'rcpt', ellipsis: true, render: (v, m) => <Typography.Link onClick={() => setOpenId(m.id)}>{v}</Typography.Link> },
        { title: '主题', dataIndex: 'subject', ellipsis: true },
        { title: '状态', dataIndex: 'status', width: 90, render: (v) => <Tag color={STATUS[v]?.[0]}>{STATUS[v]?.[1] || v}</Tag> },
        { title: 'SMTP', key: 'smtp', width: 220, ellipsis: true, render: (_, m) => (m.smtp_code ? <Tooltip title={m.smtp_response}><Text type="secondary" style={{ fontSize: 12 }}>{m.smtp_code} {m.smtp_response}</Text></Tooltip> : '-') },
        { title: '发信流', key: 's', width: 170, render: (_, m) => <StreamRef stream={{ id: m.stream_id, name: m.stream_name }} onOpen={setStreamId} /> },
        { title: '用户', key: 'u', width: 150, ellipsis: true, render: (_, m) => `#${m.user_id} ${m.username || ''}` },
        { title: '来源', dataIndex: 'source', width: 60 },
        { title: '分类', dataIndex: 'category', width: 90, ellipsis: true },
    ];

    return (
        <Card title="全站发送记录" extra={<Button icon={<ReloadOutlined />} onClick={load} />}>
            <Space wrap style={{ marginBottom: 12 }}>
                <Input allowClear placeholder="收件人 或 @域名" style={{ width: 220 }} onChange={set('rcpt')} onPressEnter={search} />
                <Input allowClear placeholder="用户 ID / 邮箱 / 用户名" style={{ width: 200 }} onChange={set('user')} onPressEnter={search} />
                <InputNumber placeholder="发信流 ID" min={1} style={{ width: 110 }} onChange={set('stream_id')} />
                <Select mode="multiple" allowClear placeholder="状态" style={{ minWidth: 150 }} onChange={set('status')}
                    options={Object.entries(STATUS).map(([k, [, l]]) => ({ value: k, label: l }))} />
                <Select allowClear placeholder="来源" style={{ width: 90 }} onChange={set('source')} options={[{ value: 'smtp', label: 'SMTP' }, { value: 'api', label: 'API' }]} />
                <Input allowClear placeholder="分类" style={{ width: 110 }} onChange={set('category')} onPressEnter={search} />
                <Input allowClear placeholder="邮件 ID" style={{ width: 240 }} onChange={set('message_id')} onPressEnter={search} />
                <DatePicker.RangePicker showTime value={range} allowClear={false}
                    disabledDate={(cur) => cur && cur > dayjs().endOf('day')}
                    onChange={(v) => {
                        if (v && v[1].diff(v[0], 'day', true) > MAX_DAYS) { message.warning(`最多 ${MAX_DAYS} 天`); return; }
                        setRange(v); setPage((p) => ({ ...p, current: 1 }));
                    }} />
                <Button type="primary" icon={<SearchOutlined />} onClick={search}>查询</Button>
            </Space>
            <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns} scroll={{ x: 1200 }}
                pagination={{
                    ...page, total: meta.total, showSizeChanger: true,
                    showTotal: (t) => (meta.capped ? `超过 ${t.toLocaleString()} 条，请缩小范围` : `共 ${t.toLocaleString()} 条`),
                    onChange: (current, pageSize) => setPage({ current, pageSize }),
                }} />
            <MessageDrawer id={openId} onClose={() => setOpenId(null)} onOpenStream={setStreamId} />
            <StreamOpsDrawer streamId={streamId} onClose={() => setStreamId(null)} />
        </Card>
    );
};

export default Messages;
